import { useState, useEffect, useCallback } from "react";
import { useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Star, ArrowLeft, RefreshCw, Shield, UtensilsCrossed, FileText, MessageSquare, Mail, Calendar } from "lucide-react";
import { toast } from "sonner";

const API = window.location.origin + "/api";
const axiosConfig = { withCredentials: true };

const AdminUserDetail = () => {
  const { userId } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);

  const fetchDetail = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/admin/users/${userId}`, axiosConfig);
      setData(response.data);
    } catch (error) {
      if (error.response?.status === 401) {
        toast.error("Session abgelaufen – bitte erneut anmelden");
        navigate("/admin");
      } else {
        toast.error("Nutzerdaten konnten nicht geladen werden");
      }
    } finally {
      setLoading(false);
    }
  }, [userId, navigate]);

  useEffect(() => {
    fetchDetail();
  }, [fetchDetail]);

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <RefreshCw className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  if (!data) {
    return (
      <div className="min-h-screen bg-background flex flex-col items-center justify-center gap-4">
        <p className="text-muted-foreground">Nutzer nicht gefunden</p>
        <Button variant="outline" onClick={() => navigate("/admin/dashboard")} data-testid="back-to-dashboard-btn">
          <ArrowLeft className="w-4 h-4 mr-2" /> Zurück zum Dashboard
        </Button>
      </div>
    );
  }

  const { user, visits, reviews, posts } = data;

  return (
    <div className="min-h-screen bg-background" data-testid="admin-user-detail-page">
      <header className="border-b border-border bg-card/50 backdrop-blur sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Star className="w-6 h-6 text-primary fill-primary" />
            <span className="text-xl font-serif text-primary">Étoile</span>
            <span className="px-2 py-1 bg-red-500/20 text-red-400 text-xs rounded-full flex items-center gap-1">
              <Shield className="w-3 h-3" /> Admin
            </span>
          </div>
          <Button variant="ghost" onClick={() => navigate("/admin/dashboard")} className="text-muted-foreground" data-testid="user-detail-back-btn">
            <ArrowLeft className="w-4 h-4 mr-2" /> Zurück
          </Button>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 py-8 space-y-6">
        <Card className="bg-card border-border" data-testid="user-detail-info-card">
          <CardHeader>
            <CardTitle className="flex items-center gap-3">
              {user.name}
              <span className="px-2 py-1 bg-primary/20 text-primary text-xs rounded">{user.badge_tier}</span>
            </CardTitle>
          </CardHeader>
          <CardContent className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 text-sm">
            <div className="flex items-center gap-2 text-muted-foreground">
              <Mail className="w-4 h-4" /> {user.email}
            </div>
            <div className="flex items-center gap-2 text-muted-foreground">
              <Star className="w-4 h-4 text-primary" /> {user.total_stars || 0} Sterne gesammelt
            </div>
            <div className="flex items-center gap-2 text-muted-foreground">
              <UtensilsCrossed className="w-4 h-4" /> {visits.length} Besuche
            </div>
            <div className="flex items-center gap-2 text-muted-foreground">
              <Calendar className="w-4 h-4" /> Registriert: {user.created_at ? new Date(user.created_at).toLocaleDateString("de-DE") : "-"}
            </div>
          </CardContent>
        </Card>

        <Card className="bg-card border-border" data-testid="user-detail-visits-card">
          <CardHeader>
            <CardTitle className="text-base flex items-center gap-2">
              <UtensilsCrossed className="w-4 h-4 text-primary" /> Besuche ({visits.length})
            </CardTitle>
          </CardHeader>
          <CardContent>
            {visits.length === 0 ? (
              <p className="text-muted-foreground text-sm">Keine Besuche vorhanden</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-border">
                      <th className="text-left p-3 text-muted-foreground text-sm">Restaurant</th>
                      <th className="text-left p-3 text-muted-foreground text-sm">Datum</th>
                      <th className="text-left p-3 text-muted-foreground text-sm">Sterne</th>
                      <th className="text-left p-3 text-muted-foreground text-sm">Bewertung</th>
                    </tr>
                  </thead>
                  <tbody>
                    {visits.map((visit) => (
                      <tr key={visit.visit_id} className="border-b border-border hover:bg-muted/50">
                        <td className="p-3">{visit.restaurant_name || visit.restaurant_id}</td>
                        <td className="p-3 text-muted-foreground">{visit.visit_date || visit.date || "-"}</td>
                        <td className="p-3 text-primary">{"★".repeat(visit.stars || 0) || "-"}</td>
                        <td className="p-3 text-muted-foreground">{visit.rating ? `${visit.rating}/5` : "-"}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <Card className="bg-card border-border" data-testid="user-detail-reviews-card">
            <CardHeader>
              <CardTitle className="text-base flex items-center gap-2">
                <FileText className="w-4 h-4 text-primary" /> Reviews ({reviews.length})
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              {reviews.length === 0 ? (
                <p className="text-muted-foreground text-sm">Keine Reviews vorhanden</p>
              ) : (
                reviews.map((review) => (
                  <div key={review.review_id} className="p-3 border border-border rounded-lg">
                    <p className="text-sm">{review.content || review.text || "-"}</p>
                    <p className="text-xs text-muted-foreground mt-1">{review.rating ? `${review.rating}/5` : ""}</p>
                  </div>
                ))
              )}
            </CardContent>
          </Card>

          <Card className="bg-card border-border" data-testid="user-detail-posts-card">
            <CardHeader>
              <CardTitle className="text-base flex items-center gap-2">
                <MessageSquare className="w-4 h-4 text-primary" /> Community-Posts ({posts.length})
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              {posts.length === 0 ? (
                <p className="text-muted-foreground text-sm">Keine Posts vorhanden</p>
              ) : (
                posts.map((post) => (
                  <div key={post.post_id} className="p-3 border border-border rounded-lg">
                    <p className="text-sm">{post.content || "-"}</p>
                    <p className="text-xs text-muted-foreground mt-1">{post.created_at ? new Date(post.created_at).toLocaleDateString("de-DE") : ""}</p>
                  </div>
                ))
              )}
            </CardContent>
          </Card>
        </div>
      </main>
    </div>
  );
};

export default AdminUserDetail;
